// src/components/ModalPagosComanda.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Button, Form, Table, Row, Col, Spinner, Alert } from "react-bootstrap";
import { getComandaById, postPagoComanda, putPagoComanda, deletePagoComanda } from "../helpers/rutaComandas";
import { getMetodos } from "../helpers/rutaMetodos";

const fmtMoney = (n) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

const unwrap = (r) => (r && r.data ? r.data : r);
const eqId = (a, b) => String(a ?? "") === String(b ?? "");

export default function ModalPagosComanda({ open, show, onClose, idComanda }) {
  const visible = (typeof open !== "undefined" ? open : show) ?? false;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comanda, setComanda] = useState(null);
  const [metodos, setMetodos] = useState([]);
  const [editId, setEditId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({
    metodoPago: "",
    montoPagado: "",
    fechaPago: new Date().toISOString().slice(0, 10),
    orden: "",
    observ: "",
  });

  // refs para Select2
  const metodoSelectRef = useRef(null);
  const syncingRef = useRef(false);

  // ------- util: refetch duro desde el back (no cache via helper ya tiene _ts) -------
  const refetchComanda = async (id) => {
    try {
      const fresh = await getComandaById(id);
      const data = unwrap(fresh);
      if (data?.ok && data?.comanda) {
        setComanda(data.comanda);
      } else {
        console.warn("[ModalPagos] refetch sin comanda:", data);
      }
    } catch (err) {
      console.error("[ModalPagos] refetch error:", err);
    }
  };

  // ------- Carga inicial al abrir -------
  useEffect(() => {
    if (!visible || !idComanda) return;
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const [c, m] = await Promise.all([getComandaById(idComanda), getMetodos()]);
        const dataC = unwrap(c);
        const dataM = unwrap(m);
        const listaMetodos = Array.isArray(dataM?.metodos) ? dataM.metodos : Array.isArray(dataM) ? dataM : [];
        setMetodos(listaMetodos.filter((x) => x?.activo));
        setComanda(dataC?.comanda ?? dataC ?? null);
      } catch (err) {
        console.error("Error cargando comanda/metodos:", err);
        setErrorMsg("No se pudo cargar la comanda o métodos de pago.");
      } finally {
        setLoading(false);
      }
    })();
  }, [visible, idComanda]);

  // ------- Init/Destroy de Select2 -------
  useEffect(() => {
    if (!visible || loading) return;
    const $ = window.$;
    const el = metodoSelectRef.current;
    if (!$ || !el) return;

    const $el = $(el);
    try { $el.select2("destroy"); } catch {}

    const $parent = $el.closest(".modal");
    $el.select2({
      width: "100%",
      theme: "bootstrap4",
      dropdownParent: $parent.length ? $parent : $(document.body),
      placeholder: "Seleccione...",
    });

    if (form.metodoPago) {
      syncingRef.current = true;
      $el.val(form.metodoPago).trigger("change.select2");
      syncingRef.current = false;
    }

    const onChange = () => {
      if (syncingRef.current) return;
      setForm((f) => ({ ...f, metodoPago: $el.val() }));
    };
    $el.on("change.select2", onChange);

    return () => {
      $el.off("change.select2", onChange);
      try { $el.select2("destroy"); } catch {}
    };
  }, [visible, loading, metodos, form.metodoPago]);

  // ------- Sync Select2 cuando cambia form.metodoPago desde React -------
  useEffect(() => {
    const $ = window.$, el = metodoSelectRef.current;
    if (!visible || loading || !$ || !el) return;
    const $el = $(el);
    const nextVal = form.metodoPago || "";
    const currentVal = $el.val() || "";
    if (currentVal !== nextVal) {
      syncingRef.current = true;
      $el.val(nextVal).trigger("change.select2");
      syncingRef.current = false;
    }
  }, [form.metodoPago, visible, loading]);

  // ------- Datos derivados -------
  const pagos = useMemo(() => (Array.isArray(comanda?.pagos) ? comanda.pagos : []), [comanda]);
  const total = Number(comanda?.total || 0);
  const totalPagado = pagos.reduce((a, p) => a + (Number(p?.montoPagado) || 0), 0);
  const saldo = total - totalPagado;

  // ------- Helpers -------
  const resetForm = () => {
    setEditId(null);
    setForm({
      metodoPago: "",
      montoPagado: "",
      fechaPago: new Date().toISOString().slice(0, 10),
      orden: "",
      observ: "",
    });
  };

  const handleHide = () => { resetForm(); onClose?.(); };

  const labelMetodo = (p) => {
    if (p?.metodoPago?.metodo) return p.metodoPago.metodo; // populado
    const f = metodos.find((x) => eqId(x._id, p?.metodoPago));
    return f?.metodo ?? "—";
    // Nota: si llega sin populate y el método ya no está activo, podría no encontrarse.
  };

  const handleEdit = (p) => {
    setEditId(p._id);
    setForm({
      metodoPago: p?.metodoPago?._id || p?.metodoPago || "",
      montoPagado: String(p?.montoPagado ?? ""),
      fechaPago: (p?.fechaPago ? new Date(p.fechaPago) : new Date()).toISOString().slice(0, 10),
      orden: p?.orden || "",
      observ: p?.observ || "",
    });
  };

  // ------- BORRAR: optimista + hard refetch -------
  const handleDelete = async (pagoId) => {
    if (!window.confirm("¿Eliminar este pago?")) return;

    const prevSnapshot = comanda;
    setErrorMsg("");

    // Optimista
    setComanda((prev) => {
      if (!prev) return prev;
      const pagosPrev = Array.isArray(prev.pagos) ? prev.pagos : [];
      return { ...prev, pagos: pagosPrev.filter((p) => !eqId(p._id, pagoId)) };
    });

    try {
      setSaving(true);
      const r = await deletePagoComanda(idComanda, pagoId);
      const data = unwrap(r);
      if (!data?.ok) {
        throw new Error(data?.error || "No ok");
      }
      // hard refetch: lo que haya realmente en DB
      await refetchComanda(idComanda);
      if (eqId(editId, pagoId)) resetForm();
    } catch (err) {
      console.error("Error al borrar pago:", err);
      setErrorMsg("No se pudo borrar el pago.");
      setComanda(prevSnapshot); // revertir
    } finally {
      setSaving(false);
    }
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // ------- GUARDAR (alta/edición): optimista + hard refetch -------
  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");

    const payload = {
      metodoPago: form.metodoPago,
      montoPagado: Number(form.montoPagado || 0),
      fechaPago: new Date(form.fechaPago).toISOString(),
      orden: form.orden?.trim() || undefined,
      observ: form.observ?.trim() || undefined,
    };

    const prevSnapshot = comanda;

    // Optimista
    if (editId) {
      setComanda((prev) => {
        if (!prev) return prev;
        const pagosPrev = Array.isArray(prev.pagos) ? prev.pagos : [];
        const nuevos = pagosPrev.map((p) =>
          eqId(p._id, editId) ? { ...p, ...payload } : p
        );
        return { ...prev, pagos: nuevos };
      });
    } else {
      const tempId = Math.random().toString(36).slice(2);
      setComanda((prev) => {
        if (!prev) return prev;
        const pagosPrev = Array.isArray(prev.pagos) ? prev.pagos : [];
        const nuevoPago = { _id: tempId, ...payload };
        return { ...prev, pagos: [...pagosPrev, nuevoPago] };
      });
    }

    try {
      setSaving(true);
      const r = editId
        ? await putPagoComanda(idComanda, editId, payload)
        : await postPagoComanda(idComanda, payload);

      const data = unwrap(r);
      if (!data?.ok) {
        throw new Error(data?.error || "No ok");
      }

      // hard refetch desde DB para asegurar persistencia real
      await refetchComanda(idComanda);

      resetForm();
    } catch (err) {
      console.error("Error al guardar pago:", err);
      setErrorMsg("No se pudo guardar el pago.");
      setComanda(prevSnapshot); // revertir optimista
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={visible} onHide={handleHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {`Pagos de la Comanda ${comanda?.codcom ? `#${comanda.codcom}` : ""}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {errorMsg ? (
              <Alert variant="danger" className="mb-3">{errorMsg}</Alert>
            ) : null}

            <Row className="mb-3">
              <Col md={4}><strong>Total:</strong> {fmtMoney(total)}</Col>
              <Col md={4}><strong>Pagado:</strong> {fmtMoney(totalPagado)}</Col>
              <Col md={4}><strong>Saldo:</strong> {fmtMoney(saldo)}</Col>
            </Row>

            <Form onSubmit={onSubmit} className="mb-3">
              <Row className="g-2">
                <Col md={4}>
                  <label className="form-label">Método</label>
                  <select
                    ref={metodoSelectRef}
                    className="select2 form-control"
                    name="metodoPago"
                    value={form.metodoPago}
                    onChange={(e) => setForm((f) => ({ ...f, metodoPago: e.target.value }))}
                    required
                    disabled={saving}
                  >
                    <option value="">Seleccione...</option>
                    {metodos.map((m) => (
                      <option key={m._id} value={m._id}>{m.metodo}</option>
                    ))}
                  </select>
                </Col>

                <Col md={2}>
                  <Form.Label>Monto</Form.Label>
                  <Form.Control
                    name="montoPagado"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.montoPagado}
                    onChange={onChange}
                    required
                    disabled={saving}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Fecha</Form.Label>
                  <Form.Control
                    name="fechaPago"
                    type="date"
                    value={form.fechaPago}
                    onChange={onChange}
                    required
                    disabled={saving}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Orden Pago</Form.Label>
                  <Form.Control
                    name="orden"
                    value={form.orden}
                    onChange={onChange}
                    placeholder="Orden de pago (opcional)"
                    disabled={saving}
                  />
                </Col>

                <Col md={12}>
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    name="observ"
                    value={form.observ}
                    onChange={onChange}
                    placeholder="Detalle del pago (opcional)"
                    disabled={saving}
                  />
                </Col>
              </Row>

              <div className="mt-3 d-flex gap-2">
                <Button type="submit" variant="success" disabled={saving}>
                  {editId ? "Guardar cambios" : "Agregar pago"}
                </Button>
                {editId && (
                  <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
                    Cancelar edición
                  </Button>
                )}
              </div>
            </Form>

            <Table striped bordered hover>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Acciones</th>
                  <th>Método</th>
                  <th className="text-end">Monto</th>
                  <th>Fecha</th>
                  <th>Orden de Pago</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {pagos.length ? pagos.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <Button size="sm" variant="primary" onClick={() => handleEdit(p)} disabled={saving}>Editar</Button>{" "}
                      <Button size="sm" variant="danger" onClick={() => handleDelete(p._id)} disabled={saving}>Borrar</Button>
                    </td>
                    <td>{labelMetodo(p)}</td>
                    <td className="text-end">{fmtMoney(p.montoPagado)}</td>
                    <td>{p?.fechaPago ? new Date(p.fechaPago).toLocaleDateString("es-AR") : "—"}</td>
                    <td>{p?.orden || ""}</td>
                    <td>{p?.observ || ""}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="text-center">Sin pagos</td></tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={saving}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
}
